<template>
  <div class="customer-service">
    <section
      v-for="item in serviceList"
      :key="item.name"
      class="service-item"
      @click="handleServiceClick(item)"
    >
      <div class="item-content">
        <img class="service-icon" :src="item.icon" :alt="item.name" />
        <span class="service-title">{{ $t(item.title) }}</span>
      </div>
      <img class="arrow-icon" src="../assets/left-arrows.png" alt="arrow" />
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { getUrlQuery } from '../utils/getUrlQuery'
import { useI18n } from 'vue-i18n'
import { Dialog } from 'vant'
import dsBridge from 'dsbridge'
import { getImageUrl, isIOS } from '..//utils/util'

// 客服配置（常量建议抽离到单独文件）
const SERVICE_CONFIG = {
  DOMESTIC: [
    {
      name: 'weChat',
      title: 'wechat',
      url: 'https://work.weixin.qq.com/kfid/kfc9b10d4e774c9f761',
      icon: require('../assets/Wechat.png'),
      analytics: '企业微信',
      dialogType: 'weChat'
    },
    {
      name: 'chtOnline',
      title: 'chat_online',
      url: 'https://work.weixin.qq.com/kfid/kfc9b10d4e774c9f761',
      icon: require('../assets/quick.png'),
      analytics: 'quick',
      isQuickChat: true
    }
  ],
  OVERSEAS: [
    {
      name: 'contactUs',
      title: 'lava_support_team',
      url: 'https://www.lavamusic.com/contact-us',
      icon: require('../assets/quick.png'),
      analytics: 'ContactUs'
    }
  ]
}

// 弹窗配置（常量建议抽离到单独文件）
const DIALOG_CONFIG = {
  weChat: {
    qrCode: 'WechatQR.png',
    saveImageUrl: 'https://qn.lavamusic.com/qr/WechatQR.png',
    template: (t) => ({
      message: h('div', { class: 'dialog-container' }, [
        h('p', t('customer.dialog.wechat.needSupport')),
        h('img', {
          class: 'qr-code',
          src: getImageUrl('WechatQR.png'),
          style: { width: '120px', height: '120px', margin: '8px 0' }
        }),
        h(
          'p',
          { class: 'qr-tip', style: { color: '#666', fontSize: '12px' } },
          t('customer.dialog.wechat.withWeChat')
        )
      ]),
      confirmText: t('customer.dialog.wechat.saveTheQR')
    })
  }
}

// 国际化
const { t, locale } = useI18n()

// 响应式数据
const currentLang = getUrlQuery('lang')
const currentRegion = getUrlQuery('region')
const scriptChat = ref(null)

// 计算属性：根据地区切换客服列表
const serviceList = computed(() => {
  locale.value = currentLang === 'cn' ? 'cn' : 'en' // 设置默认语言
  return currentRegion === 'zh'
    ? SERVICE_CONFIG.DOMESTIC
    : SERVICE_CONFIG.OVERSEAS
})

// 埋点逻辑
const handleAnalytics = (eventName, params) => {
  dsBridge.call('analytics', { event: eventName, params })
}

// 保存图片（异步处理）
const handleSaveImage = async (url) => {
  try {
    await dsBridge.call('saveFileWithUrl', { type: 'image', url })
  } catch (error) {
    console.error('图片保存失败:', error)
  }
}

// 显示弹窗
const showDialog = (item) => {
  const { dialogType } = item
  if (!dialogType || !DIALOG_CONFIG[dialogType]) return

  const { template } = DIALOG_CONFIG[dialogType]
  const { message, confirmText } = template(t) // 传入国际化函数

  Dialog.alert({
    message,
    confirmButtonText,
    confirmButtonColor: '#000'
  }).then(() => handleSaveImage(DIALOG_CONFIG[dialogType].saveImageUrl))
}

// 点击事件处理
const handleServiceClick = (item) => {
  if (item.isQuickChat) {
    window.quickChatApi?.chat()
    handleAnalytics('customer_service_type', {
      CUSTOMER_SERVICE_TYPE: item.analytics
    })
    return
  }

  showDialog(item)
  handleAnalytics('customer_service_type', {
    CUSTOMER_SERVICE_TYPE: item.analytics
  })
  openExternalUrl(item.url)
}

// 打开外部链接
const openExternalUrl = (url) => {
  const isZH = currentRegion === 'zh'
  const isIOSs = isIOS()

  if (isIOSs || isZH) {
    dsBridge.call('openMediator', {
      target: 'Web',
      action: 'ExternalWeb',
      params: { url }
    })
  } else {
    window.location.href = url
  }
}

// 生命周期 - 挂载时初始化在线客服
onMounted(() => {
  if (currentRegion === 'zh') {
    const script = document.createElement('script')
    script.src =
      'https://chat.quickcep.com/initQuickChat.js?platform=others&accessId=d83afaf2-e013-4c33-a645-76e065bf9e7f'
    script.async = true
    script.onload = () => {
      quickChatLoaded.value = true
      // 确保 API 可用
      if (window.quickChatApi) {
        console.log('QuickChat 初始化完成')
      }
    }
    script.onerror = (error) => {
      quickChatLoaded.value = false
      console.error('QuickChat 加载失败:', error)
    }
    document.head.appendChild(script)
    scriptChat.value = script
  }
})

// 生命周期 - 卸载时清理资源
onUnmounted(() => {
  scriptChat.value?.remove() // 移除动态添加的脚本标签
})
</script>

<style lang="scss" scoped>
.customer-service {
  padding: 10px 16px 0;
  background: #f7f7f7;
  min-height: 100vh;
}

.service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 72px;
  background: white;
  border-radius: 8px;
  margin-bottom: 16px;
  cursor: pointer;

  .item-content {
    display: flex;
    align-items: center;
    flex: 1;
  }

  .service-icon {
    @apply mr-2;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    margin-right: 8px;
  }

  .service-title {
    font-size: 16px;
    line-height: 24px;
  }

  .arrow-icon {
    width: 6.5px;
  }
}

.dialog-container {
  @apply flex flex-col items-center;
  display: flex;
  align-items: center;
  flex-direction: column;

  .qr-code {
    @apply my-2;
    width: 120px;
    height: 120px;
  }

  .qr-tip {
    @apply text-xs text-gray-600;
  }
}
</style>